import { useState, useMemo, useEffect } from "react";
import { createNotification } from "@/lib/createNotification";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { invalidateFinancials } from "@/lib/invalidateProjectFinancials";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Loader2, Send, RefreshCw, AlertTriangle, Calendar, Check, DollarSign, ChevronDown, ChevronRight, History, FileText, Printer, XCircle, Pencil, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { createBillApproval } from "@/lib/createBillApproval";
import { cn } from "@/lib/utils";
import { useBillPaymentLog } from "@/hooks/useBillPaymentLog";
import { usePermissions } from "@/hooks/usePermissions";
import { useCompanyProfile } from "@/hooks/useCompanyProfile";
import { useVoidBill, useHardDeleteBill } from "@/hooks/useBills";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { exportPdf } from "@/lib/export";
import { BillPrintTemplate } from "@/components/shared/print-templates/BillPrintTemplate";
import { groupBillLinesForPrint } from "@/lib/groupBillLinesForPrint";
import BillAttachments from "./BillAttachments";

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);

interface Props {
  wo: any;
  projectId: string;
  onMutate: () => void;
  onOpenCreateBill: () => void;
}

const STATUS_BADGE: Record<string, { label: string; className: string }> = {
  draft:          { label: "Draft Bill",             className: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300" },
  submitted:      { label: "Pending Approval",        className: "bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300" },
  sent:           { label: "Pending Approval",        className: "bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300" },
  approved:       { label: "Approved",               className: "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300" },
  scheduled:      { label: "Scheduled for Payment",  className: "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300" },
  paid:           { label: "Paid",                   className: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300" },
  partially_paid: { label: "Partially Paid",         className: "bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300" },
  rejected:       { label: "Rejected",               className: "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300" },
  void:           { label: "Void",                   className: "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400" },
  voided:         { label: "Voided",                 className: "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400" },
};

const PAYMENT_TERMS_OPTIONS = [
  { value: "net_7",  label: "Net 7 (7 days)" },
  { value: "net_14", label: "Net 14 (14 days)" },
  { value: "net_20", label: "Net 20 (20 days)" },
  { value: "net_30", label: "Net 30 (30 days)" },
];

const ACTION_LABEL: Record<string, string> = {
  approved: "Approved",
  paid: "Paid",
  partially_paid: "Partial Payment",
};

// ── Helpers: parse "H-TS-002 Entry Thresh — Unit 547" → parent + child ──
const UNIT_DELIMITER = /\s*[—–-]\s*(?=Unit\s)/i;

function parseLineDescription(description: string): { parent: string; child: string | null } {
  const match = description.match(UNIT_DELIMITER);
  if (match && match.index != null) {
    const parent = description.slice(0, match.index).trim();
    const child = description.slice(match.index + match[0].length).trim();
    return { parent, child };
  }
  return { parent: description, child: null };
}

interface GroupedSOV {
  sovLabel: string;
  parentLabel: string;
  unitPrice: number;
  totalQty: number;
  totalAmount: number;
  children: {
    id: string;
    childLabel: string | null;
    quantity: number;
    unitPrice: number;
    lineTotal: number;
  }[];
}

const r2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

function groupLines(lines: any[]): GroupedSOV[] {
  const map = new Map<string, GroupedSOV>();

  for (const line of lines) {
    const { parent, child } = parseLineDescription(line.description ?? "");
    const sovLabel = line.sov_lines?.description || parent;
    // Group key: combine SOV label + unit price to keep different-priced items separate
    const groupKey = `${sovLabel}::${Number(line.unit_price)}`;

    if (!map.has(groupKey)) {
      map.set(groupKey, {
        sovLabel,
        parentLabel: parent,
        unitPrice: r2(Number(line.unit_price)),
        totalQty: 0,
        totalAmount: 0,
        children: [],
      });
    }
    const g = map.get(groupKey)!;
    g.totalQty = r2(g.totalQty + Number(line.quantity));
    g.totalAmount = r2(g.totalAmount + Number(line.line_total));
    g.children.push({
      id: line.id,
      childLabel: child,
      quantity: r2(Number(line.quantity)),
      unitPrice: r2(Number(line.unit_price)),
      lineTotal: r2(Number(line.line_total)),
    });
  }

  return Array.from(map.values());
}

// ── Expandable parent row ──────────────────────────────────────────────────
function GroupRow({ group }: { group: GroupedSOV }) {
  const [open, setOpen] = useState(false);
  const hasChildren = group.children.length > 1 || group.children[0]?.childLabel != null;

  return (
    <>
      {/* Parent summary row */}
      <tr
        className={cn(
          "border-b",
          hasChildren && "cursor-pointer hover:bg-muted/30 transition-colors",
        )}
        onClick={() => hasChildren && setOpen((o) => !o)}
      >
        <td className="px-2 py-1.5 font-medium">
          <div className="flex items-center gap-1.5">
            {hasChildren && (
              <span className="text-muted-foreground">
                {open
                  ? <ChevronDown className="h-3.5 w-3.5" />
                  : <ChevronRight className="h-3.5 w-3.5" />}
              </span>
            )}
            <span className="truncate max-w-[180px]" title={group.parentLabel}>
              {group.parentLabel}
            </span>
            {hasChildren && (
              <Badge variant="secondary" className="ml-1 text-[10px] px-1 py-0 leading-tight font-normal">
                {group.children.length}
              </Badge>
            )}
          </div>
        </td>
        <td className="px-2 py-1.5 text-muted-foreground truncate max-w-[150px]" title={group.sovLabel}>
          {group.sovLabel}
        </td>
        <td className="px-2 py-1.5 text-right font-mono">{r2(group.totalQty)}</td>
        <td className="px-2 py-1.5 text-right font-mono">{fmt(group.unitPrice)}</td>
        <td className="px-2 py-1.5 text-right font-mono font-medium">{fmt(group.totalAmount)}</td>
      </tr>

      {/* Child unit rows */}
      {open && hasChildren && group.children.map((child) => (
        <tr key={child.id} className="border-b last:border-0 bg-muted/20">
          <td className="pl-8 pr-2 py-1 text-muted-foreground">
            {child.childLabel ?? group.parentLabel}
          </td>
          <td className="px-2 py-1" />
          <td className="px-2 py-1 text-right font-mono text-muted-foreground">{r2(child.quantity)}</td>
          <td className="px-2 py-1 text-right font-mono text-muted-foreground">{fmt(child.unitPrice)}</td>
          <td className="px-2 py-1 text-right font-mono text-muted-foreground">{fmt(child.lineTotal)}</td>
        </tr>
      ))}
    </>
  );
}

// ── Expandable Line Items ──────────────────────────────────────────────────
function BillLineItems({ billId }: { billId: string }) {
  const { data: lines, isLoading } = useQuery({
    queryKey: ["bill_line_items", billId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bill_lines")
        .select("id, description, quantity, unit_price, line_total, sov_line_id, sov_lines(description)")
        .eq("bill_id", billId)
        .order("created_at");
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 5 * 60 * 1000,
  });

  const groups = useMemo(() => (lines ? groupLines(lines) : []), [lines]);

  if (isLoading || !lines || lines.length === 0) return null;

  return (
    <Collapsible>
      <CollapsibleTrigger className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors group">
        <FileText className="h-3.5 w-3.5" />
        <span>Line Items ({lines.length})</span>
        <ChevronDown className="h-3 w-3 transition-transform group-data-[state=open]:rotate-180" />
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-2">
        <div className="border rounded-md overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-muted/50 border-b">
                <th className="text-left px-2 py-1.5 font-medium">Description</th>
                <th className="text-left px-2 py-1.5 font-medium">SOV Line</th>
                <th className="text-right px-2 py-1.5 font-medium">Qty</th>
                <th className="text-right px-2 py-1.5 font-medium">Unit Price</th>
                <th className="text-right px-2 py-1.5 font-medium">Amount</th>
              </tr>
            </thead>
            <tbody>
              {groups.map((g, i) => (
                <GroupRow key={`${g.sovLabel}-${g.unitPrice}-${i}`} group={g} />
              ))}
            </tbody>
          </table>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

// ── Payment History ──────────────────────────────────────────────────
function BillPaymentHistory({ billId }: { billId: string }) {
  const { data: logs, isLoading } = useBillPaymentLog(billId);

  if (isLoading || !logs || logs.length === 0) return null;

  return (
    <Collapsible>
      <CollapsibleTrigger className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors group">
        <History className="h-3.5 w-3.5" />
        <span>Payment History ({logs.length})</span>
        <ChevronDown className="h-3 w-3 transition-transform group-data-[state=open]:rotate-180" />
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-2">
        <div className="border rounded-md overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-muted/50 border-b">
                <th className="text-left px-2 py-1.5 font-medium">Action</th>
                <th className="text-left px-2 py-1.5 font-medium">Line Item</th>
                <th className="text-right px-2 py-1.5 font-medium">Line Amt</th>
                <th className="text-right px-2 py-1.5 font-medium">Paid</th>
                <th className="text-left px-2 py-1.5 font-medium">Method</th>
                <th className="text-left px-2 py-1.5 font-medium">Reference</th>
                <th className="text-left px-2 py-1.5 font-medium">Date</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <tr key={log.id} className="border-b last:border-0">
                  <td className="px-2 py-1.5">
                    <Badge variant="outline" className="text-[10px]">
                      {ACTION_LABEL[log.action] ?? log.action}
                    </Badge>
                  </td>
                  <td className="px-2 py-1.5 max-w-[200px] truncate" title={log.line_description ?? ""}>
                    {log.line_description || "—"}
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono">{fmt(Number(log.line_amount))}</td>
                  <td className="px-2 py-1.5 text-right font-mono">
                    {Number(log.payment_amount) > 0 ? fmt(Number(log.payment_amount)) : "—"}
                  </td>
                  <td className="px-2 py-1.5">{log.payment_method?.toUpperCase() ?? "—"}</td>
                  <td className="px-2 py-1.5 truncate max-w-[120px]" title={log.payment_reference ?? ""}>
                    {log.payment_reference || "—"}
                  </td>
                  <td className="px-2 py-1.5 text-muted-foreground">
                    {log.payment_date ? new Date(log.payment_date).toLocaleDateString() : new Date(log.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

// ── Print helper ──────────────────────────────────────────────────────
async function printWOBill(
  bill: any,
  wo: any,
  companyProfile?: { logo_url?: string | null; name?: string; address?: string | null; city?: string | null; state?: string | null; zip?: string | null } | null,
) {
  const { data: lines } = await supabase
    .from("bill_lines")
    .select("description, quantity, unit_price, line_total")
    .eq("bill_id", bill.id)
    .order("created_at");

  const r2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

  const lineItems = groupBillLinesForPrint(lines ?? []);


  // Use bill's stored totals (corrected by DB trigger)
  const printSubtotal = r2(Number(bill.subtotal));
  const printRetPct = r2(Number(bill.retainage_percent ?? 0));
  const printRetAmt = r2(Number(bill.retainage_amount));
  const printTotal = r2(Number(bill.total));

  exportPdf(
    <BillPrintTemplate
      data={{
        bill_number: bill.bill_number,
        bill_date: bill.bill_date,
        due_date: bill.due_date ?? bill.payment_due_date,
        status: bill.status,
        subtotal: printSubtotal,
        retainage_amount: printRetAmt,
        retainage_percent: printRetPct,
        total: printTotal,
        vendor_name: wo.vendors?.name ?? wo.vendor_name,
        project_name: wo.projects?.name ?? wo.project_name,
        project_number: wo.projects?.project_number ?? wo.project_number,
        company_name: companyProfile?.name,
        company_logo_url: companyProfile?.logo_url,
        company_address: companyProfile?.address,
        company_city: companyProfile?.city,
        company_state: companyProfile?.state,
        company_zip: companyProfile?.zip,
        description: bill.description,
        payment_terms: bill.payment_terms,
        line_items: lineItems,
      }}
    />,
    `Bill ${bill.bill_number}`
  );
}

// Customer-priced variant: replaces unit_price with the SOV/customer rate from work_order_lines.
async function printWOBillCustomerPrices(
  bill: any,
  wo: any,
  companyProfile?: { logo_url?: string | null; name?: string; address?: string | null; city?: string | null; state?: string | null; zip?: string | null } | null,
) {
  const { data: lines } = await (supabase as any)
    .from("bill_lines")
    .select("description, quantity, unit_price, line_total, work_order_line_id, sov_line_id, work_order_lines(id, sov_line_id, contract_rate, quantity), sov_lines(unit_price, quantity)")
    .eq("bill_id", bill.id)
    .order("created_at");

  const r2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

  // Collect unique sov_line_ids referenced by these bill lines so we can compute
  // each WOL's share of its parent SOV line (when multiple WOLs split one SOV item).
  const sovIds = Array.from(new Set(
    (lines ?? [])
      .map((l: any) => l.work_order_lines?.sov_line_id ?? l.sov_line_id)
      .filter(Boolean)
  )) as string[];

  let siblingMap = new Map<string, { totalContract: number }>();
  if (sovIds.length) {
    const { data: siblings } = await (supabase as any)
      .from("work_order_lines")
      .select("sov_line_id, contract_rate, quantity")
      .in("sov_line_id", sovIds);
    for (const s of siblings ?? []) {
      const k = s.sov_line_id as string;
      const cv = (Number(s.contract_rate) || 0) * (Number(s.quantity) || 0);
      const prev = siblingMap.get(k)?.totalContract ?? 0;
      siblingMap.set(k, { totalContract: prev + cv });
    }
  }

  const rewritten = (lines ?? []).map((l: any) => {
    const wol = l.work_order_lines;
    const sov = l.sov_lines;
    const billedTotal = Number(l.line_total) || 0;
    const wolContract = (Number(wol?.contract_rate) || 0) * (Number(wol?.quantity) || 0);
    const sovTotal = (Number(sov?.unit_price) || 0) * (Number(sov?.quantity) || 0);
    const sovKey = wol?.sov_line_id ?? l.sov_line_id;
    const sumContracts = sovKey ? (siblingMap.get(sovKey)?.totalContract ?? wolContract) : wolContract;

    if (sovTotal > 0 && wolContract > 0 && sumContracts > 0) {
      const wolShare = wolContract / sumContracts;            // this WOL's slice of the SOV line
      const wolCustomerVal = sovTotal * wolShare;             // customer value of this WOL
      const ratioBilled = billedTotal / wolContract;          // portion billed this bill
      const customerAmount = r2(wolCustomerVal * ratioBilled);
      return {
        description: l.description,
        quantity: 1,
        unit_price: customerAmount,
        line_total: customerAmount,
      };
    }
    // Fallback: stored values
    return {
      description: l.description,
      quantity: Number(l.quantity) || 0,
      unit_price: Number(l.unit_price) || 0,
      line_total: r2(billedTotal),
    };
  });

  const lineItems = groupBillLinesForPrint(rewritten);

  // Customer-facing bill: no retainage row.
  const subtotal = r2(lineItems.reduce((s, li) => s + Number(li.line_total), 0));
  const total = subtotal;

  exportPdf(
    <BillPrintTemplate
      data={{
        bill_number: bill.bill_number,
        bill_date: bill.bill_date,
        due_date: bill.due_date ?? bill.payment_due_date,
        status: bill.status,
        subtotal,
        retainage_amount: 0,
        retainage_percent: 0,
        total,
        vendor_name: wo.vendors?.name ?? wo.vendor_name,
        project_name: wo.projects?.name ?? wo.project_name,
        project_number: wo.projects?.project_number ?? wo.project_number,
        company_name: companyProfile?.name,
        company_logo_url: companyProfile?.logo_url,
        company_address: companyProfile?.address,
        company_city: companyProfile?.city,
        company_state: companyProfile?.state,
        company_zip: companyProfile?.zip,
        description: bill.description,
        payment_terms: bill.payment_terms,
        line_items: lineItems,
      }}
    />,
    `Bill ${bill.bill_number} (customer prices)`
  );
}



// ── Edit Bill Dialog ──────────────────────────────────────────────────
interface EditLineState {
  id: string;
  description: string;
  quantity: number;
  unit_price: number;
  line_total: number;
  work_order_line_id: string | null;
}

function EditBillDialog({
  bill,
  onClose,
  onSaved,
}: {
  bill: any;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [billDate, setBillDate] = useState(bill.bill_date ?? new Date().toISOString().split("T")[0]);
  const [dueDate, setDueDate] = useState(bill.due_date ?? bill.payment_due_date ?? "");
  const [description, setDescription] = useState(bill.description ?? "");
  const [retainagePct, setRetainagePct] = useState(Number(bill.retainage_percent ?? 0));
  const [lines, setLines] = useState<EditLineState[]>([]);
  const [deletedLines, setDeletedLines] = useState<EditLineState[]>([]);
  const [saving, setSaving] = useState(false);
  const [linesLoading, setLinesLoading] = useState(true);

  // Fetch line items on mount and auto-correct stale totals
  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("bill_lines")
        .select("id, description, quantity, unit_price, line_total, work_order_line_id")
        .eq("bill_id", bill.id)
        .order("created_at");

      setLines(
        (data ?? []).map((l: any) => ({
          id: l.id,
          description: l.description ?? "",
          quantity: Number(l.quantity),
          unit_price: Number(l.unit_price),
          line_total: Number(l.quantity) * Number(l.unit_price),
          work_order_line_id: l.work_order_line_id ?? null,
        })),
      );
      setLinesLoading(false);
    })();
  }, [bill.id]);

  const updateLine = (idx: number, field: keyof EditLineState, value: string | number) => {
    setLines((prev) => {
      const next = [...prev];
      const row = { ...next[idx], [field]: value };
      if (field === "quantity" || field === "unit_price") {
        row.line_total = Number(row.quantity) * Number(row.unit_price);
      }
      next[idx] = row;
      return next;
    });
  };

  const deleteLine = (idx: number) => {
    setLines((prev) => {
      const removed = prev[idx];
      setDeletedLines((d) => [...d, removed]);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const subtotal = Math.round((lines.reduce((s, l) => s + l.line_total, 0) + Number.EPSILON) * 100) / 100;
  const retainageAmt = Math.round((subtotal * retainagePct / 100 + Number.EPSILON) * 100) / 100;
  const total = Math.round((subtotal - retainageAmt + Number.EPSILON) * 100) / 100;

  const handleSave = async () => {
    setSaving(true);
    try {
      const { error: editErr } = await supabase.rpc("edit_bill" as any, {
        p_bill_id: bill.id,
        p_header: {
          bill_date: billDate,
          due_date: dueDate || null,
          description: description || null,
          retainage_percent: retainagePct,
          retainage_amount: retainageAmt,
          subtotal,
          total,
        },
        p_lines: lines.map((l) => ({
          id: l.id,
          description: l.description,
          quantity: l.quantity,
          unit_price: l.unit_price,
          line_total: l.line_total,
        })),
        p_new_lines: [],
        p_deleted_lines: deletedLines.map((dl) => ({
          id: dl.id,
          work_order_line_id: dl.work_order_line_id ?? null,
        })),
      });
      if (editErr) throw editErr;

      // Audit log
      try {
        const { data: companyId } = await supabase.rpc("my_company_id");
        const userId = (await supabase.auth.getUser()).data.user?.id;
        await supabase.from("ops_audit_log").insert({
          company_id: companyId,
          table_name: "bills",
          record_id: bill.id,
          action: "bill_edited",
          changed_by: userId,
          metadata: { bill_number: bill.bill_number, subtotal, retainage_amount: retainageAmt, total },
        } as any);
      } catch { /* non-blocking */ }

      toast.success(`${bill.bill_number} updated`);
      onSaved();
      onClose();
    } catch (e: any) {
      toast.error(e.message ?? "Failed to save bill");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Bill — {bill.bill_number}</DialogTitle>
          <DialogDescription>
            Edit bill details and line items. Changes apply immediately on save.
          </DialogDescription>
        </DialogHeader>

        {/* Header fields */}
        <div className="grid grid-cols-2 gap-4 py-2">
          <div className="space-y-1.5">
            <Label>Bill Date</Label>
            <Input type="date" value={billDate} onChange={(e) => setBillDate(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Due Date</Label>
            <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
          </div>
          <div className="col-span-2 space-y-1.5">
            <Label>Description</Label>
            <Textarea
              placeholder="Optional bill description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Retainage %</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={retainagePct}
              onChange={(e) => setRetainagePct(Number(e.target.value))}
            />
          </div>
        </div>

        {/* Line items */}
        <div className="space-y-2">
          <Label className="text-sm font-semibold">Line Items</Label>
          {linesLoading ? (
            <div className="flex justify-center py-4">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="border rounded-md overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-muted/50 border-b">
                    <th className="text-left px-2 py-1.5 font-medium w-[38%]">Description</th>
                    <th className="text-right px-2 py-1.5 font-medium w-[14%]">Qty</th>
                    <th className="text-right px-2 py-1.5 font-medium w-[18%]">Unit Price</th>
                    <th className="text-right px-2 py-1.5 font-medium w-[18%]">Amount</th>
                    <th className="w-[12%]" />
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line, idx) => (
                    <tr key={line.id} className="border-b last:border-0">
                      <td className="px-1.5 py-1">
                        <Input
                          className="h-7 text-xs"
                          value={line.description}
                          onChange={(e) => updateLine(idx, "description", e.target.value)}
                        />
                      </td>
                      <td className="px-1.5 py-1">
                        <Input
                          className="h-7 text-xs text-right font-mono"
                          type="number"
                          step="0.01"
                          min="0"
                          value={line.quantity}
                          onChange={(e) => updateLine(idx, "quantity", Number(e.target.value))}
                        />
                      </td>
                      <td className="px-1.5 py-1">
                        <Input
                          className="h-7 text-xs text-right font-mono"
                          type="number"
                          step="0.01"
                          min="0"
                          value={line.unit_price}
                          onChange={(e) => updateLine(idx, "unit_price", Number(e.target.value))}
                        />
                      </td>
                      <td className="px-2 py-1 text-right font-mono text-sm font-medium">
                        {fmt(line.line_total)}
                      </td>
                      <td className="px-1.5 py-1 text-center">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 text-muted-foreground hover:text-destructive"
                          onClick={() => deleteLine(idx)}
                          title="Remove line item"
                          disabled={lines.length <= 1}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Deleted lines notice */}
        {deletedLines.length > 0 && (
          <div className="flex items-start gap-2 p-2 rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 text-xs">
            <AlertTriangle className="h-3.5 w-3.5 text-amber-600 mt-0.5 shrink-0" />
            <div>
              <p className="font-medium text-amber-800 dark:text-amber-300">
                {deletedLines.length} line{deletedLines.length > 1 ? "s" : ""} will be removed on save.
              </p>
              <p className="text-amber-700 dark:text-amber-400">
                Linked work order lines will be restored to billable status.
              </p>
            </div>
          </div>
        )}

        {/* Totals */}
        <div className="flex justify-end pt-2">
          <div className="w-64 text-sm space-y-1">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Subtotal</span>
              <span className="font-semibold font-mono">{fmt(subtotal)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Retainage ({retainagePct}%)</span>
              <span className="font-mono text-red-600">({fmt(retainageAmt)})</span>
            </div>
            <div className="flex justify-between border-t pt-1 font-bold">
              <span>Total</span>
              <span className="font-mono">{fmt(total)}</span>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving || !billDate}>
            {saving ? <><Loader2 className="mr-1 h-4 w-4 animate-spin" />Saving...</> : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Main Component ──────────────────────────────────────────────────
export const WOBillsList = ({ wo, projectId, onMutate, onOpenCreateBill }: Props) => {
  const queryClient = useQueryClient();
  const { can, isAdmin } = usePermissions();
  const { data: companyProfile } = useCompanyProfile();
  const voidBill = useVoidBill();
  const hardDeleteBill = useHardDeleteBill();

  const [sendingId, setSendingId] = useState<string | null>(null);
  const [schedulingBill, setSchedulingBill] = useState<any | null>(null);
  const [paymentTerms, setPaymentTerms] = useState("net_30");
  const [payingBill, setPayingBill] = useState<any | null>(null);
  const [payDate, setPayDate] = useState(new Date().toISOString().split("T")[0]);
  const [payRef, setPayRef] = useState("");
  const [payMethod, setPayMethod] = useState("ach");
  const [payAmount, setPayAmount] = useState("");
  const [payingLoading, setPayingLoading] = useState(false);
  const [voidingBill, setVoidingBill] = useState<any | null>(null);
  const [voidReason, setVoidReason] = useState("");
  const [editingBill, setEditingBill] = useState<any | null>(null);
  const [hardDeletingBill, setHardDeletingBill] = useState<any | null>(null);

  const canVoid = isAdmin || can("void_bills");

  const { data: bills, isLoading } = useQuery({
    queryKey: ["wo_bills", wo.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bills")
        .select("id, bill_number, bill_date, due_date, status, subtotal, retainage_amount, retainage_percent, total, description, rejection_reason, void_reason, revision_number, sent_at, created_at, payment_terms, payment_due_date, scheduled_at, paid_at, amount_paid, payment_method, payment_reference, vendor_id, vendors(name)")
        .eq("work_order_id", wo.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });

  const handleSendForApproval = async (bill: any) => {
    setSendingId(bill.id);
    try {
      const { data: companyId } = await supabase.rpc("my_company_id");
      if (!companyId) throw new Error("Company not found");
      const userId = (await supabase.auth.getUser()).data.user?.id;

      const { error: updateErr } = await supabase.rpc("update_bill_status" as any, {
        p_bill_id: bill.id,
        p_updates: { status: "sent", sent_at: new Date().toISOString(), sent_by: userId },
      });
      if (updateErr) throw updateErr;

      await createBillApproval({
        companyId,
        requestType: "bill_approve",
        title: `Bill ${bill.bill_number} — ${wo.number}`,
        projectId,
        linkedWoId: wo.id,
        linkedBillId: bill.id,
        requestData: {
          subtotal: Number(bill.subtotal),
          retainage: Number(bill.retainage_amount),
          total: Number(bill.total),
          revision_number: bill.revision_number ?? 0,
        },
        createdBy: userId,
      });

      await supabase.from("ops_audit_log").insert({
        company_id: companyId,
        table_name: "bills",
        record_id: bill.id,
        action: "bill_sent_for_approval",
        old_status: "draft",
        new_status: "sent",
        changed_by: userId,
        metadata: {
          wo_number: wo.number,
          bill_number: bill.bill_number,
          total: Number(bill.total),
        },
      } as any);

      toast.success(`${bill.bill_number} sent for approval`);
      createNotification({
        entity_type: "bill", entity_id: bill.id, entity_label: bill.bill_number,
        project_id: projectId, type: "bill_sent_for_approval",
        title: "Bill Sent for Approval",
        message: `${bill.bill_number} (${fmt(Number(bill.total))}) submitted for approval.`,
        priority: "normal", action_label: "Review",
        action_url: `/controller/projects/${projectId}?tab=work_orders`,
        created_by: userId,
      });
      invalidateFinancials(queryClient, { billId: bill.id, projectId });
      onMutate();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setSendingId(null);
    }
  };

  const handleSchedulePayment = async () => {
    if (!schedulingBill) return;
    try {
      const { error } = await supabase.rpc("schedule_bill" as any, {
        p_bill_id: schedulingBill.id,
        p_payment_terms: paymentTerms,
      } as any);
      if (error) {
        const termsMap: Record<string, number> = { net_7: 7, net_14: 14, net_20: 20, net_30: 30 };
        const days = termsMap[paymentTerms] ?? 30;
        const dueDate = new Date();
        dueDate.setDate(dueDate.getDate() + days);
        const { error: updateErr } = await supabase.rpc("update_bill_status" as any, {
          p_bill_id: schedulingBill.id,
          p_updates: {
            status: "scheduled",
            payment_terms: paymentTerms,
            payment_due_date: dueDate.toISOString().split("T")[0],
            scheduled_at: new Date().toISOString(),
          },
        });
        if (updateErr) throw updateErr;
      }
      toast.success(`${schedulingBill.bill_number} scheduled for payment`);
      invalidateFinancials(queryClient, { billId: schedulingBill.id, projectId });
      onMutate();
      setSchedulingBill(null);
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  const handlePaySubmit = async () => {
    if (!payingBill) return;
    setPayingLoading(true);
    try {
      const billTotal = Number(payingBill.total ?? 0);
      const amt = payAmount === "" ? billTotal : Number(payAmount);
      const finalStatus = amt < billTotal ? "partially_paid" : "paid";
      const userId = (await supabase.auth.getUser()).data.user?.id;
      const { data: companyId } = await supabase.rpc("my_company_id");

      const { error } = await supabase.rpc("update_bill_status" as any, {
        p_bill_id: payingBill.id,
        p_updates: {
          status: finalStatus,
          paid_at: new Date(payDate).toISOString(),
          paid_by: userId,
          payment_reference: payRef.trim() || null,
          payment_method: payMethod,
          amount_paid: amt,
        },
      });
      if (error) throw error;

      try {
        await supabase.from("ops_audit_log").insert({
          company_id: companyId,
          table_name: "bills", record_id: payingBill.id,
          action: finalStatus === "paid" ? "bill_paid" : "bill_partially_paid",
          old_status: payingBill.status, new_status: finalStatus,
          changed_by: userId,
          metadata: { payment_reference: payRef.trim() || null, payment_method: payMethod, amount_paid: amt },
        } as any);
      } catch { /* non-blocking */ }

      if (companyId) {
        try {
          const { data: lines } = await supabase
            .from("bill_lines")
            .select("id, description, line_total")
            .eq("bill_id", payingBill.id);
          if (lines && lines.length > 0) {
            const rows = lines.map((line: any) => {
              const lineTotal = Number(line.line_total ?? 0);
              const proportion = billTotal > 0 ? lineTotal / billTotal : 0;
              return {
                company_id: companyId,
                bill_id: payingBill.id,
                bill_line_id: line.id,
                action: finalStatus,
                line_description: line.description ?? null,
                line_amount: lineTotal,
                payment_amount: Number((amt * proportion).toFixed(2)),
                payment_method: payMethod,
                payment_reference: payRef.trim() || null,
                payment_date: new Date(payDate).toISOString(),
                recorded_by: userId ?? null,
              };
            });
            await supabase.from("bill_payment_log").insert(rows as any);
          }
        } catch { /* non-blocking */ }
      }

      toast.success(`${payingBill.bill_number} ${finalStatus === "partially_paid" ? "partially " : ""}marked as paid`);
      invalidateFinancials(queryClient, { billId: payingBill.id, projectId });
      onMutate();
      setPayingBill(null);
      setPayRef("");
      setPayAmount("");
      setPayMethod("ach");
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setPayingLoading(false);
    }
  };

  const openPayDialog = (bill: any) => {
    setPayingBill(bill);
    setPayDate(new Date().toISOString().split("T")[0]);
    setPayRef("");
    setPayAmount("");
    setPayMethod("ach");
  };

  const handleReviseAndResubmit = (bill: any) => {
    toast.info(`Revising ${bill.bill_number} — WO lines restored. Create a new bill with corrections.`);
    onOpenCreateBill();
  };

  const handleVoidSubmit = async () => {
    if (!voidingBill || !voidReason.trim()) return;
    try {
      await voidBill.mutateAsync({ billId: voidingBill.id, reason: voidReason.trim() });
      toast.success(`${voidingBill.bill_number} voided`);
      invalidateFinancials(queryClient, { billId: voidingBill.id, projectId });
      onMutate();
      setVoidingBill(null);
      setVoidReason("");
    } catch (e: any) {
      toast.error(e.message ?? "Failed to void bill");
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!bills || bills.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground text-sm">
        No bills created for this work order yet.
      </div>
    );
  }

  const payBillTotal = Number(payingBill?.total ?? 0);
  const payEffective = payAmount === "" ? payBillTotal : Number(payAmount);
  const payIsPartial = payEffective > 0 && payEffective < payBillTotal;

  return (
    <div className="space-y-3">
      {/* Schedule Payment Dialog */}
      <Dialog open={!!schedulingBill} onOpenChange={(o) => !o && setSchedulingBill(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Schedule Payment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Bill <span className="font-mono font-semibold">{schedulingBill?.bill_number}</span> · Total {fmt(Number(schedulingBill?.total ?? 0))}
            </p>
            <div>
              <Label>Payment Terms</Label>
              <Select value={paymentTerms} onValueChange={setPaymentTerms}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PAYMENT_TERMS_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSchedulingBill(null)}>Cancel</Button>
            <Button onClick={handleSchedulePayment}>
              <Calendar className="h-4 w-4 mr-1" /> Schedule Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Record Payment Dialog */}
      <Dialog open={!!payingBill} onOpenChange={(o) => !o && setPayingBill(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
            <DialogDescription>
              Bill <span className="font-mono font-semibold">{payingBill?.bill_number}</span> — Total {fmt(payBillTotal)}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Payment Date</Label>
              <Input type="date" value={payDate} onChange={(e) => setPayDate(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Payment Method</Label>
              <select
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                value={payMethod}
                onChange={(e) => setPayMethod(e.target.value)}
              >
                <option value="ach">ACH</option>
                <option value="check">Check</option>
                <option value="wire">Wire Transfer</option>
                <option value="credit_card">Credit Card</option>
                <option value="cash">Cash</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <Label>Amount</Label>
              <Input
                type="number"
                step="0.01"
                min="0.01"
                max={payBillTotal}
                placeholder={fmt(payBillTotal)}
                value={payAmount}
                onChange={(e) => setPayAmount(e.target.value)}
              />
              {payIsPartial && (
                <p className="text-xs text-amber-600">Partial payment — bill will be marked as partially paid.</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Payment Reference</Label>
              <Input placeholder="Check #, confirmation, etc." value={payRef} onChange={(e) => setPayRef(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayingBill(null)}>Cancel</Button>
            <Button onClick={handlePaySubmit} disabled={payingLoading || payEffective <= 0}>
              <DollarSign className="h-4 w-4 mr-1" />
              {payingLoading ? "Saving…" : payIsPartial ? "Record Partial Payment" : "Confirm Payment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Void Bill Dialog */}
      <Dialog open={!!voidingBill} onOpenChange={(o) => !o && setVoidingBill(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-4 w-4" /> Void Bill
            </DialogTitle>
            <DialogDescription>
              Void bill <span className="font-mono font-semibold">{voidingBill?.bill_number}</span>. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5 py-2">
            <Label>Reason for voiding <span className="text-destructive">*</span></Label>
            <Textarea
              placeholder="Describe why this bill is being voided…"
              value={voidReason}
              onChange={(e) => setVoidReason(e.target.value)}
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVoidingBill(null)}>Cancel</Button>
            <Button
              variant="destructive"
              disabled={!voidReason.trim() || voidBill.isPending}
              onClick={handleVoidSubmit}
            >
              <XCircle className="h-4 w-4 mr-1" />
              {voidBill.isPending ? "Voiding…" : "Void Bill"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Hard Delete Confirm */}
      <AlertDialog open={!!hardDeletingBill} onOpenChange={(o) => !o && setHardDeletingBill(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <Trash2 className="h-4 w-4" /> Permanently Delete Bill?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will <strong>permanently remove</strong>{" "}
              <span className="font-mono font-semibold">{hardDeletingBill?.bill_number}</span> and all its line items from the database.
              Any linked work order lines will be restored to billable status.
              This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={hardDeleteBill.isPending}
              onClick={async (e) => {
                e.preventDefault();
                try {
                  await hardDeleteBill.mutateAsync(hardDeletingBill.id);
                  toast.success(`${hardDeletingBill.bill_number} permanently deleted`);
                  setHardDeletingBill(null);
                  onMutate();
                } catch (err: any) {
                  toast.error(err.message ?? "Failed to delete bill");
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {hardDeleteBill.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Trash2 className="mr-1 h-4 w-4" />}
              Delete Permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Bill Dialog */}
      {editingBill && (
        <EditBillDialog
          bill={editingBill}
          onClose={() => setEditingBill(null)}
          onSaved={() => {
            invalidateFinancials(queryClient, { billId: editingBill.id, projectId });
            onMutate();
          }}
        />
      )}

      {bills.map((bill: any) => {
        const status = bill.status as string;
        const badge = STATUS_BADGE[status] ?? STATUS_BADGE.draft;
        const isSending = sendingId === bill.id;
        const isVoided = status === "voided" || status === "void";
        const isPaid = status === "paid";
        const vendorName = bill.vendors?.name;
        const displaySubtotal = isVoided ? 0 : Number(bill.subtotal ?? 0);
        const displayRetainage = isVoided ? 0 : Number(bill.retainage_amount ?? 0);
        const displayTotal = isVoided ? 0 : Number(bill.total ?? 0);

        return (
          <div
            key={bill.id}
            className={cn(
              "border rounded-md p-3 space-y-2",
              status === "rejected" && "border-red-300 bg-red-50/50 dark:border-red-800 dark:bg-red-950/30",
              status === "approved" && "border-green-200 bg-green-50/30 dark:border-green-800 dark:bg-green-950/20",
              status === "scheduled" && "border-blue-200 bg-blue-50/30 dark:border-blue-800 dark:bg-blue-950/20",
              isVoided && "opacity-50",
            )}
          >
            {/* Header row */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="font-mono text-sm font-medium">{bill.bill_number}</span>
                <Badge variant="outline" className={cn("text-[10px]", badge.className)}>
                  {badge.label}
                </Badge>
                {(bill.revision_number ?? 0) > 0 && (
                  <Badge variant="outline" className="text-[10px] bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300">
                    Rev {bill.revision_number}
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">{bill.bill_date}</span>
                {bill.due_date && (
                  <span className="text-xs text-muted-foreground">· Due {bill.due_date}</span>
                )}
              </div>
            </div>

            {/* Vendor name if available */}
            {vendorName && (
              <div className="text-xs text-muted-foreground">
                Vendor: <span className="font-medium text-foreground">{vendorName}</span>
              </div>
            )}

            {/* Amounts */}
            <div className="flex gap-4 text-sm flex-wrap">
              <span>Subtotal: <span className="font-medium">{fmt(displaySubtotal)}</span></span>
              <span>Retainage ({bill.retainage_percent ?? 0}%): <span className="text-muted-foreground">{fmt(displayRetainage)}</span></span>
              <span>Total: <span className="font-semibold">{fmt(displayTotal)}</span></span>
              {Number(bill.amount_paid) > 0 && (
                <span>Paid: <span className="font-medium text-emerald-600">{fmt(Number(bill.amount_paid))}</span></span>
              )}
            </div>

            {/* Description */}
            {bill.description && (
              <p className="text-xs text-muted-foreground italic">{bill.description}</p>
            )}

            {/* Payment info for scheduled/paid bills */}
            {(status === "scheduled" || status === "paid" || status === "partially_paid") && bill.payment_due_date && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Calendar className="h-3.5 w-3.5" />
                <span>{bill.payment_terms?.replace("_", " ").toUpperCase()} · Due {bill.payment_due_date}</span>
                {(status === "paid" || status === "partially_paid") && bill.paid_at && (
                  <span className="text-emerald-600 font-medium">
                    · {bill.payment_method?.toUpperCase()} · {new Date(bill.paid_at).toLocaleDateString()}
                    {bill.payment_reference && ` · Ref: ${bill.payment_reference}`}
                  </span>
                )}
              </div>
            )}

            {/* Rejection reason */}
            {status === "rejected" && bill.rejection_reason && (
              <div className="flex items-start gap-2 text-sm bg-red-100/50 dark:bg-red-900/30 rounded p-2">
                <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                <div>
                  <span className="font-medium text-red-700 dark:text-red-400">Rejection reason: </span>
                  <span className="text-red-600 dark:text-red-300">{bill.rejection_reason}</span>
                </div>
              </div>
            )}

            {/* Void reason */}
            {isVoided && bill.void_reason && (
              <div className="flex items-start gap-2 text-sm bg-muted/50 rounded p-2">
                <AlertTriangle className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                <div>
                  <span className="font-medium text-muted-foreground">Void reason: </span>
                  <span className="text-muted-foreground">{bill.void_reason}</span>
                </div>
              </div>
            )}

            {/* Expandable Line Items */}
            <BillLineItems billId={bill.id} />

            {/* Actions */}
            <div className="flex gap-2 pt-1 flex-wrap items-center">
              {/* Print — always available */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" variant="ghost" className="text-muted-foreground">
                    <Printer className="mr-1 h-3.5 w-3.5" />
                    Print
                    <ChevronDown className="ml-1 h-3.5 w-3.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  <DropdownMenuItem onClick={() => printWOBill(bill, wo, companyProfile)}>
                    Contractor prices
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => printWOBillCustomerPrices(bill, wo, companyProfile)}>
                    Customer prices
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Edit — always available */}
              <Button
                size="sm"
                variant="ghost"
                className="text-muted-foreground"
                onClick={() => setEditingBill(bill)}
              >
                <Pencil className="mr-1 h-3.5 w-3.5" />
                Edit
              </Button>

              {status === "draft" && (
                <Button size="sm" variant="default" onClick={() => handleSendForApproval(bill)} disabled={isSending}>
                  {isSending ? (
                    <><Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />Sending...</>
                  ) : (
                    <><Send className="mr-1 h-3.5 w-3.5" />Send for Approval</>
                  )}
                </Button>
              )}
              {status === "approved" && (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-blue-300 text-blue-700 hover:bg-blue-50 dark:border-blue-700 dark:text-blue-400"
                    onClick={() => { setSchedulingBill(bill); setPaymentTerms("net_30"); }}
                  >
                    <Calendar className="mr-1 h-3.5 w-3.5" />
                    Schedule Payment
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-700 dark:text-emerald-400"
                    onClick={() => openPayDialog(bill)}
                  >
                    <DollarSign className="mr-1 h-3.5 w-3.5" />
                    Mark as Paid
                  </Button>
                </>
              )}
              {(status === "scheduled" || status === "partially_paid") && (
                <Button
                  size="sm"
                  variant="outline"
                  className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-700 dark:text-emerald-400"
                  onClick={() => openPayDialog(bill)}
                >
                  <DollarSign className="mr-1 h-3.5 w-3.5" />
                  {status === "partially_paid" ? "Record Additional Payment" : "Mark as Paid"}
                </Button>
              )}
              {status === "rejected" && (
                <Button
                  size="sm"
                  variant="outline"
                  className="border-red-300 text-red-700 hover:bg-red-50 dark:border-red-700 dark:text-red-400 dark:hover:bg-red-950"
                  onClick={() => handleReviseAndResubmit(bill)}
                >
                  <RefreshCw className="mr-1 h-3.5 w-3.5" />
                  Revise & Resubmit
                </Button>
              )}

              {/* Void action — permission-gated */}
              {canVoid && !isVoided && !isPaid && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-destructive hover:text-destructive"
                  onClick={() => { setVoidingBill(bill); setVoidReason(""); }}
                >
                  <XCircle className="mr-1 h-3.5 w-3.5" />
                  Void
                </Button>
              )}

              {/* Hard Delete — admin only */}
              {isAdmin && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-destructive hover:text-destructive"
                  onClick={() => setHardDeletingBill(bill)}
                >
                  <Trash2 className="mr-1 h-3.5 w-3.5" />
                  Hard Delete
                </Button>
              )}
            </div>

            {/* Payment History */}
            {(status === "approved" || status === "paid" || status === "partially_paid" || status === "scheduled") && (
              <BillPaymentHistory billId={bill.id} />
            )}

            {/* Attachments */}
            <BillAttachments billId={bill.id} />
          </div>
        );
      })}
    </div>
  );
};
